import {
  Bar,
  BarChart as RechartsBarChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';

import type { ChartLabelFormat, NormalizedBarChartData } from '../shared/types';

import {
  CHART_AXIS,
  CHART_DIMENSIONS,
  CHART_VALUE_LABEL_TEXT,
} from '../shared/chart-tokens';
import { formatValue } from '../shared/format-value';
import { InvalidChart } from '../shared/invalid-chart';
import { getSeriesSignSummary } from '../shared/chart-helpers';
import { validateBarChartProps } from '../shared/chart-validation';

import { BarChartLegend } from './bar-chart-legend';

import { BarChartBar } from './bar-chart-bar';
import { BarChartTick } from './bar-chart-tick';

export interface BarChartData {
  categoryAxis: { label?: string; categories: readonly string[] };
  series: ReadonlyArray<{
    label?: string;
    values: ReadonlyArray<number | null>;
  }>;
  format?: ChartLabelFormat;
}

export interface BarChartProps {
  data: BarChartData;
  title?: string;
  animate?: boolean;
  className?: string;
}

const CATEGORY_KEY = 'category';

const seriesKeyAt = (seriesIndex: number): string => `series-${seriesIndex}`;

interface ChartRow {
  [CATEGORY_KEY]: string;
  [seriesSlug: string]: string | number | null;
}

const toRowOrientedData = (
  normalized: NormalizedBarChartData
): readonly ChartRow[] =>
  normalized.categoryAxis.categories.map((category, categoryIndex) => {
    const row: ChartRow = { [CATEGORY_KEY]: category };

    normalized.series.forEach((singleSeries, seriesIndex) => {
      row[seriesKeyAt(seriesIndex)] = singleSeries.values[categoryIndex];
    });

    return row;
  });

const buildAriaLabel = (
  normalized: NormalizedBarChartData,
  title?: string
): string => {
  const seriesCount = normalized.series.length;
  const categoryCount = normalized.categoryAxis.categories.length;
  const categoryNoun = categoryCount === 1 ? 'category' : 'categories';

  const subject = title ? `${title} bar chart` : 'Bar chart';
  const axisLabel = normalized.categoryAxis.label;
  const subjectWithAxis = axisLabel ? `${subject} by ${axisLabel}` : subject;

  return `${subjectWithAxis}, ${seriesCount} series across ${categoryCount} ${categoryNoun}`;
};

export const BarChart = ({
  data,
  title,
  animate = true,
  className,
}: BarChartProps) => {
  const validation = validateBarChartProps(data);

  if (!validation.valid) {
    return <InvalidChart className={className} />;
  }

  const { normalized } = validation;
  const signSummary = getSeriesSignSummary(normalized.series);
  const dataCrossesZero = signSummary.hasPositive && signSummary.hasNegative;
  const chartData = toRowOrientedData(normalized);
  const seriesCount = normalized.series.length;

  return (
    <div className={className}>
      <BarChartLegend
        series={normalized.series}
        signSummary={signSummary}
        className="mt-3"
      />
      <ResponsiveContainer width="100%" height={CHART_DIMENSIONS.defaultHeight}>
        <RechartsBarChart
          data={chartData as ChartRow[]}
          margin={{
            top: CHART_DIMENSIONS.marginTop,
            right: CHART_DIMENSIONS.marginRight,
            left: CHART_DIMENSIONS.marginLeft,
            bottom: CHART_DIMENSIONS.marginBottom,
          }}
          barGap={CHART_DIMENSIONS.barGap}
          accessibilityLayer={true}
          aria-label={buildAriaLabel(normalized, title)}
        >
          {/* Screen readers announce bar values during keyboard navigation. */}
          <Tooltip
            trigger="click"
            formatter={(value) =>
              typeof value === 'number'
                ? formatValue(value, normalized.format)
                : '—'
            }
            wrapperClassName="sr-only"
            cursor={false}
          />
          <XAxis
            dataKey={CATEGORY_KEY}
            axisLine={false}
            tickLine={false}
            interval={0}
            height={CHART_DIMENSIONS.xAxisLabelHeight}
            tick={<BarChartTick />}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            width="auto"
            padding={{
              top: CHART_DIMENSIONS.yAxisLabelPaddingTop,
              bottom: dataCrossesZero
                ? CHART_DIMENSIONS.yAxisLabelPaddingBottom
                : 0,
            }}
            tick={{
              fill: CHART_AXIS.tickColor,
              fontFamily: CHART_AXIS.tickFontFamily,
              fontSize: CHART_AXIS.tickFontSize,
            }}
          />
          {dataCrossesZero && (
            <ReferenceLine y={0} stroke={CHART_AXIS.zeroLineColor} />
          )}
          {normalized.series.map((singleSeries, seriesIndex) => {
            const dataKey = seriesKeyAt(seriesIndex);

            return (
              <Bar
                key={dataKey}
                dataKey={dataKey}
                name={singleSeries.label}
                isAnimationActive={animate}
                shape={(shapeProps) => {
                  const { value, ...rest } = shapeProps;
                  const scalarValue = typeof value === 'number' ? value : null;

                  return (
                    <BarChartBar
                      {...rest}
                      value={scalarValue}
                      seriesIndex={seriesIndex}
                      seriesCount={seriesCount}
                    />
                  );
                }}
              >
                <LabelList
                  dataKey={dataKey}
                  position="top"
                  formatter={(value) =>
                    typeof value === 'number'
                      ? formatValue(value, normalized.format)
                      : ''
                  }
                  {...CHART_VALUE_LABEL_TEXT}
                />
              </Bar>
            );
          })}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};
